import { serializeLinkedContext } from '../../../contracts/projects/linked-context';
import { projectsComposerSubmitPrompts } from '../build-projects-composer-message';
import { composerLinkedContext } from '../composer-linked-context';
import type { ProjectsComposerPromptDraft } from '../projects-composer-editor-draft';
import type { ComposerTurn } from './turn-command.machine';

const isPureTextDraft = (draft: ProjectsComposerPromptDraft): boolean =>
  draft.segments.every((segment) => segment.kind === 'text');

const draftFromBody = (body: string): ProjectsComposerPromptDraft => ({
  text: body,
  segments: [{ kind: 'text', text: body }],
});

type CanonicalPrompts = {
  readonly wireText: string;
  readonly displayText: string;
};

const canonicalizeDraft = (
  draft: ProjectsComposerPromptDraft,
  linkedItems: ComposerTurn['linkedItems']
): CanonicalPrompts => {
  const { wireText, displayText } = projectsComposerSubmitPrompts({
    draft,
    linkedContextPreamble: serializeLinkedContext(
      composerLinkedContext(draft, linkedItems)
    ),
  });
  return { wireText, displayText };
};

/**
 * True when the canonical pure-text rebuild of `draft` reproduces this turn's
 * wire text and effective display/preview (what `buildTurn` would have set).
 */
const matchesCanonicalSemantics = (
  turn: ComposerTurn,
  canonical: CanonicalPrompts
): boolean => {
  const turnDisplay = turn.displayText ?? turn.text;
  return (
    canonical.wireText === turn.text &&
    canonical.displayText === turnDisplay &&
    turn.previewText === canonical.displayText
  );
};

/**
 * Free-text body the inline queue editor may change.
 *
 * Eligibility is proved by a canonical round-trip: rebuild the CURRENT draft
 * with the production linked-context normalization + prompt builders and
 * require exact wire + display/preview parity. Make-as-workflow, skills,
 * note-push plans, and other specialized shapes fail that proof and omit Edit.
 */
export const getQueuedTurnEditableBody = (turn: ComposerTurn): string | null => {
  // These fields carry semantics that a plain text editor cannot rebuild.
  // Keep the explicit gate even when the current strings happen to round-trip:
  // externally enqueued turns may only carry a flattened restore draft.
  if (turn.routineName || turn.computerUseSkill || turn.notePushPlan) return null;
  if (!isPureTextDraft(turn.draft)) return null;
  if (turn.restoreContext && !isPureTextDraft(turn.restoreContext.draft)) return null;

  const body = turn.draft.text;
  if (body.trim().length === 0) return null;

  const linkedItems = turn.restoreContext?.linkedItems ?? turn.linkedItems;
  const canonical = canonicalizeDraft(turn.draft, linkedItems);
  if (!matchesCanonicalSemantics(turn, canonical)) return null;

  return body;
};

/**
 * Rebuild a queued turn after an inline body edit. Callers pass the resulting
 * `ComposerTurn` across `UPDATE_QUEUED` — the machine must not reconstruct
 * strings itself.
 *
 * Package-internal; composition uses `getQueuedTurnEditableBody` only.
 */
export const rebuildQueuedTurnFromBodyEdit = (
  turn: ComposerTurn,
  nextBody: string
): ComposerTurn | null => {
  if (getQueuedTurnEditableBody(turn) === null) return null;

  const trimmed = nextBody.trim();
  if (!trimmed) return null;

  const nextDraft = draftFromBody(trimmed);
  const linkedItems = turn.restoreContext?.linkedItems ?? turn.linkedItems;
  const { wireText, displayText } = canonicalizeDraft(nextDraft, linkedItems);

  const nextRestore = {
    draft: nextDraft,
    linkedItems,
  };

  return {
    ...turn,
    text: wireText,
    displayText: displayText !== wireText ? displayText : undefined,
    previewText: displayText,
    draft: nextDraft,
    linkedItems,
    restoreContext: turn.restoreContext
      ? { ...turn.restoreContext, ...nextRestore }
      : nextRestore,
  };
};
